import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import LoginForm from './components/Auth/LoginForm'
import ProtectedRoute from './components/Common/ProtectedRoute'
import Layout from './components/Layout/Layout'
import { AuthProvider } from './contexts/AuthContext'
import { DirectionProvider, useDirection } from './context/DirectionContext'
import CycleDetail from './pages/CycleDetail'
import Cycles from './pages/Cycles'
import Dashboard from './pages/Dashboard'
import FormBuilder from './pages/FormBuilder'
import ParametersPage from './pages/ParametersPage'
import StationBuilder from './pages/StationBuilder'
import StatementBuilder from './pages/StatementBuilder'
import SummaryPage from './pages/SummaryPage'
import Units from './pages/Units'
import Users from './pages/Users'
import { createAppTheme } from './theme/theme'

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			staleTime: 5 * 60 * 1000,
		},
	},
})

/** Reads DirectionContext to build a direction-aware MUI theme — must be inside DirectionProvider */
function ThemedApp() {
	const { dir } = useDirection()
	const appTheme = useMemo(() => createAppTheme(dir), [dir])

	return (
		<ThemeProvider theme={appTheme}>
			<CssBaseline />
			<AuthProvider>
				<Router>
					<Routes>
						<Route path="/login" element={<LoginForm />} />
						<Route path="/" element={<Navigate to="/dashboard" replace />} />
						<Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
						<Route path="/users"     element={<ProtectedRoute><Layout><Users /></Layout></ProtectedRoute>} />
						<Route path="/cycles"    element={<ProtectedRoute><Layout><Cycles /></Layout></ProtectedRoute>} />
						<Route path="/cycles/:cycleId/*" element={<ProtectedRoute><Layout><CycleDetail /></Layout></ProtectedRoute>} />
						<Route path="/units"     element={<ProtectedRoute><Layout><Units /></Layout></ProtectedRoute>} />
						<Route path="/summary"   element={<ProtectedRoute><Layout><SummaryPage /></Layout></ProtectedRoute>} />
						<Route path="/forms/builder"    element={<ProtectedRoute><Layout><FormBuilder /></Layout></ProtectedRoute>} />
						<Route path="/stations/builder" element={<ProtectedRoute><Layout><StationBuilder /></Layout></ProtectedRoute>} />
						<Route path="/statements/builder" element={<ProtectedRoute><Layout><StatementBuilder /></Layout></ProtectedRoute>} />
						<Route path="/parameters"       element={<ProtectedRoute><Layout><ParametersPage /></Layout></ProtectedRoute>} />
						<Route path="*" element={<Navigate to="/dashboard" replace />} />
					</Routes>
				</Router>
			</AuthProvider>
		</ThemeProvider>
	)
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<DirectionProvider>
				<ThemedApp />
			</DirectionProvider>
		</QueryClientProvider>
	)
}

export default App
